// ============================================================================
// Ask AI's hiring card — a step on one hiring request (item #944, the hiring
// part; TCSD-0531 one tool per part, saved through the screen's own function).
// The server half is supabase/functions/am-copilot/hiring.ts.
//
// Every save here is the function the Hiring pipeline calls
// (src/modules/hr-hiring/hooks/use-hiring-data.ts): the stage move and "Mark as
// no longer needed" (setHiringStatus), and "Add another opening like this"
// (addOpening).
// ============================================================================

import { addOpening, setHiringStatus } from '../hr-hiring/hooks/use-hiring-data';
import { HIRING_STAGE_LABELS, type HiringRequestStatus } from '../../shared/types';
import { rpcErrorMessage } from '../../shared/rpc-error';
import { hiringButtonWords } from '../../../supabase/functions/am-copilot/buttons';
import type { FieldSpec } from './building-actions';

export const HIRING_ACTIONS = ['move_stage', 'add_opening', 'mark_not_needed'] as const;
export type HiringAction = (typeof HIRING_ACTIONS)[number];

/** A draft as the server persisted it (prepareHiringAction). */
export interface HiringDraft {
  request_id: string;
  action: HiringAction;
  fields: Record<string, unknown>;
  display_name?: string | null;
  context?: Record<string, unknown>;
}

export interface HiringCardSpec {
  title: string;
  fields: FieldSpec[];
  info: Array<{ label: string; value: string }>;
  readBack: string | null;
  doItLabel: string | null;
  missing: string | null;
}

const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v));
const ctxOf = (d: HiringDraft) => (d.context ?? {}) as Record<string, unknown>;
const words = (s: string) => HIRING_STAGE_LABELS[s as HiringRequestStatus] ?? s;

/** The request as the board names it: its title, and the building. */
export const hiringOf = (d: HiringDraft) => str(ctxOf(d).title) || str(d.display_name) || d.request_id;

/** What the card shows for this draft. */
export function hiringCard(d: HiringDraft, f: Record<string, unknown>): HiringCardSpec {
  const c = ctxOf(d);
  const info = [
    { label: 'Building', value: str(c.building) || '—' },
    { label: 'Now', value: words(str(c.status) || 'No_Status') },
  ];
  const base: HiringCardSpec = { title: '', fields: [], info, readBack: null, doItLabel: hiringButtonWords(d.action, f), missing: null };
  switch (d.action) {
    case 'move_stage':
      return { ...base, title: `Move to ${words(str(f.to))}` };
    case 'add_opening':
      return { ...base, title: 'Add another opening like this', readBack: `This opens one more request for the same job, Open, next to ${d.request_id}.` };
    case 'mark_not_needed':
      return { ...base, title: 'Mark as no longer needed', readBack: 'This moves the request to Disregarded, off the active board.' };
  }
}

export interface HiringCommitResult {
  ok: boolean;
  error?: string;
  note?: string;
  target_table?: string;
  target_row_id?: string | null;
  committed_payload?: Record<string, unknown>;
}
const fail = (error: string): HiringCommitResult => ({ ok: false, error });

/** Save one hiring draft through the Hiring pipeline's own function. */
export async function commitHiringAction(d: HiringDraft, f: Record<string, unknown>): Promise<HiringCommitResult> {
  const id = d.request_id;
  const ok = (note: string, committed_payload: Record<string, unknown>): HiringCommitResult =>
    ({ ok: true, note, target_table: 'hiring_requests', target_row_id: id, committed_payload });
  try {
    switch (d.action) {
      case 'move_stage':
        await setHiringStatus(id, str(f.to) as HiringRequestStatus);
        return ok(`${hiringOf(d)} → ${words(str(f.to))}.`, { status: str(f.to) });
      case 'add_opening': {
        const headcount = Number(ctxOf(d).headcount ?? 1) || 1;
        await addOpening({ request_id: id, headcount });
        return ok('Added another opening for the same job.', { headcount: headcount + 1 });
      }
      case 'mark_not_needed':
        await setHiringStatus(id, 'Disregarded');
        return ok('Marked as no longer needed.', { status: 'Disregarded' });
    }
  } catch (e) {
    return fail(rpcErrorMessage(e, 'That did not save.'));
  }
}
